/**
 * LiveKit-backed jukebox broadcaster. Replaces the hand-rolled
 * `MusicBroadcaster` peer-mesh.
 *
 * The host's `<audio>` element (driven by `MusicPlayer`) is captured
 * via `HTMLMediaElement.captureStream()` and published to the same
 * LiveKit Room the user's voice is on, as a separate audio Track
 * named `"music"`. LiveKit fans the track out to subscribed
 * listeners — no per-listener `RTCPeerConnection`, no signaling
 * relay through the zone channel.
 *
 * Phase 7 of the LiveKit migration. Lives parallel to the existing
 * `MusicBroadcaster` until Phase 8 deletes the mesh.
 *
 * See [docs/guides/spatial-audio-livekit.md](../../../../docs/guides/spatial-audio-livekit.md).
 */

import { type LocalTrackPublication, Track, type Room } from "livekit-client";
import type { MusicPlayer } from "./MusicPlayer";
import type { ZoneChannelHandle } from "@/net/zone-channel";

interface LiveKitMusicBroadcasterOptions {
  player: MusicPlayer;
  /**
   * Already-connected LiveKit Room from `LiveKitVoiceEngineSession.getRoom()`.
   * Reusing the voice connection for music keeps everything on one
   * `RTCPeerConnection` and one ICE/DTLS handshake.
   */
  room: Room;
  /**
   * Existing zone channel — the host still pushes `music:playing` /
   * `music:track_changed` / `music:stopped` intents so listeners can
   * render the playlist UI. The audio plane goes through LiveKit;
   * the metadata plane stays on the channel until Phase 8.
   */
  channel: ZoneChannelHandle;
  selfId: string;
}

export class LiveKitMusicBroadcaster {
  private readonly player: MusicPlayer;
  private readonly room: Room;
  private readonly channel: ZoneChannelHandle;
  private readonly selfId: string;
  private readonly unsubscribePlayer: () => void;

  private publication: LocalTrackPublication | null = null;
  private capturedStream: MediaStream | null = null;
  private broadcasting = false;

  constructor(opts: LiveKitMusicBroadcasterOptions) {
    this.player = opts.player;
    this.room = opts.room;
    this.channel = opts.channel;
    this.selfId = opts.selfId;

    this.unsubscribePlayer = this.player.addCallbacks({
      onTrackChange: (track, index) => {
        if (this.broadcasting) {
          this.channel.sendMusicTrackChanged(track.name, index);
        }
      },
      onPlayStateChange: (playing) => {
        if (playing && !this.broadcasting) {
          void this.startBroadcast();
        } else if (!playing && this.broadcasting) {
          void this.stopBroadcast();
        }
      },
    });
  }

  destroy(): void {
    this.unsubscribePlayer();
    void this.stopBroadcast();
  }

  private async startBroadcast(): Promise<void> {
    if (this.broadcasting) return;

    const playlist = this.player.getPlaylist();
    const idx = this.player.getCurrentIndex();
    const current = playlist[idx];
    if (!current) return;

    try {
      // MusicPlayer already routes its audio through a
      // MediaStreamDestination node — no extra captureStream() needed.
      this.capturedStream = this.player.getMediaStream();
      const [audioTrack] = this.capturedStream.getAudioTracks();
      if (!audioTrack) throw new Error("MusicPlayer.getMediaStream returned no audio tracks");

      this.publication = await this.room.localParticipant.publishTrack(audioTrack, {
        // LiveKit catch-all for non-mic audio. The track `name` is
        // what `Mythglade.Audio.LiveKit` matches on for MusicRegistry
        // wiring (see audio/livekit.ex apply_webhook track_published).
        source: Track.Source.Unknown,
        name: "music",
      });

      this.broadcasting = true;

      // Listeners still discover playlist + current-track over the
      // zone channel — the LiveKit track only carries audio bytes.
      this.channel.sendMusicPlaying(
        current.name,
        playlist.map((t) => t.name),
      );
    } catch (err) {
      console.warn("[LiveKitMusicBroadcaster] startBroadcast failed:", err);
      this.cleanup();
    }
  }

  private async stopBroadcast(): Promise<void> {
    if (!this.broadcasting) return;
    this.broadcasting = false;

    try {
      if (this.publication?.track) {
        await this.room.localParticipant.unpublishTrack(this.publication.track);
      }
    } catch (err) {
      console.warn("[LiveKitMusicBroadcaster] unpublishTrack failed:", err);
    } finally {
      this.cleanup();
      this.channel.sendMusicStopped();
    }
  }

  private cleanup(): void {
    this.publication = null;
    // MusicPlayer owns the underlying MediaStream — don't stop its
    // tracks here, that would freeze the player. Just drop the
    // reference.
    this.capturedStream = null;
  }

  /** True iff a music track is currently published on the LiveKit Room. */
  get isBroadcasting(): boolean {
    return this.broadcasting;
  }

  /** Self-id helper — surfaced for symmetry with MusicBroadcaster. */
  getSelfId(): string {
    return this.selfId;
  }
}
